import { useCallback, useMemo, useState } from "react";
import {
  type NavigateResultPayload,
  type NavigateToNodePayload,
} from "@claudafinil/shared/contract";
import { useAuthSessionStore } from "./features/auth";
import { useTimelineStore } from "./features/timeline";
import { useShellConnection } from "./hooks/use-shell-connection";
import { useRemoteAuthActions } from "./hooks/use-remote-auth-actions";
import { useConversationStats } from "./hooks/use-conversation-stats";
import { useAnnotationsLoader } from "./hooks/use-annotations-loader";
import { KnownIssueBanner } from "./components/remote-layout/KnownIssueBanner";
import { RemoteHeader } from "./components/remote-layout/RemoteHeader";
import { RemoteTabs } from "./components/remote-layout/RemoteTabs";
import { TroubleshootingPanel } from "./components/remote-layout/TroubleshootingPanel";
import { Button } from "./components/ui/button";
import {
  SupportRequestDialog,
  type SupportRequestFormInput,
} from "./features/support";
import type { ApiResult } from "./lib/api";

interface AppProps {
  isExtensionRoute: boolean;
}

function LoadingView(): JSX.Element {
  return (
    <div className="flex h-full min-h-full flex-col items-center justify-center gap-3 bg-background p-6 text-center">
      <div
        className="h-7 w-7 animate-remote-spin rounded-full border-[3px] border-input border-t-foreground"
        aria-hidden
      />
      <p className="m-0 text-sm text-muted-foreground">
        Claudafinil Remote UI - Connecting...
      </p>
    </div>
  );
}

interface CenteredStateViewProps {
  message: string;
  actionLabel?: string;
  onAction?: () => void;
}

function CenteredStateView({
  message,
  actionLabel,
  onAction,
}: Readonly<CenteredStateViewProps>): JSX.Element {
  return (
    <div className="flex h-full min-h-full flex-col items-center justify-center gap-3 bg-background p-6 text-center">
      <p className="m-0 text-sm text-muted-foreground">{message}</p>
      {actionLabel && onAction ? (
        <Button onClick={onAction}>{actionLabel}</Button>
      ) : null}
    </div>
  );
}

export default function App({ isExtensionRoute }: AppProps): JSX.Element {
  const [isTroubleshootingOpen, setIsTroubleshootingOpen] = useState(false);
  const [isSupportDialogOpen, setIsSupportDialogOpen] = useState(false);

  const {
    connect: connectToShell,
    connectionState,
    selectedVersion,
    extensionVersion,
    adapterConfigStatus,
    commandsRef,
    runtimeApiClientRef,
  } = useShellConnection();
  const {
    pendingAuthAction,
    authErrorMessage,
    signIn,
    signOut,
    resetAuthUiState,
  } = useRemoteAuthActions({ commandsRef, runtimeApiClientRef });

  const { conversationName, metadataText } = useConversationStats();

  const isAuthenticated = useAuthSessionStore((state) => state.isAuthenticated);
  const authUser = useAuthSessionStore((state) => state.user);
  const hasAccessToken = useAuthSessionStore((state) =>
    Boolean(state.accessToken),
  );
  const sessionEmail = useMemo(() => {
    const email = authUser?.email;
    return typeof email === "string" && email.trim() ? email.trim() : null;
  }, [authUser]);
  const sessionName = useMemo(() => {
    const name = authUser?.name;
    return typeof name === "string" && name.trim() ? name.trim() : null;
  }, [authUser]);

  const isDebugMode = useMemo(() => {
    if (typeof window === "undefined") {
      return false;
    }

    return new URLSearchParams(window.location.search).get("debug") === "1";
  }, []);
  const showTroubleshootingEntry = isDebugMode || import.meta.env.DEV;
  useAnnotationsLoader(connectionState);

  const navigateToTimelineNode = useCallback(
    async (targetNodeIdOrText: string): Promise<NavigateResultPayload> => {
      const commands = commandsRef.current;
      if (!commands) {
        return {
          success: false,
          error: "Shell connection not available.",
        };
      }

      try {
        return await commands.navigateToNode(targetNodeIdOrText);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Navigation request failed.";
        return {
          success: false,
          error: message,
        };
      }
    },
    [],
  );

  const navigateToAnnotation = useCallback(
    async (target: NavigateToNodePayload): Promise<NavigateResultPayload> => {
      const commands = commandsRef.current;
      if (!commands) {
        return {
          success: false,
          error: "Shell connection not available.",
        };
      }

      try {
        return await commands.navigateToAnnotation(target);
      } catch (error) {
        return {
          success: false,
          error:
            error instanceof Error ? error.message : "Navigation request failed.",
        };
      }
    },
    [],
  );

  const refreshConversationTree = useCallback(
    async (): Promise<{ success: boolean; error?: string }> => {
      const commands = commandsRef.current;
      if (!commands) {
        return {
          success: false,
          error: "Shell connection not available.",
        };
      }

      const store = useTimelineStore.getState();
      store.setLoading(true);
      store.setError(null);
      try {
        const result = await commands.refreshConversation();
        useTimelineStore.getState().setLoading(false);
        useTimelineStore
          .getState()
          .setError(result.success ? null : result.error ?? null);
        return result;
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Refresh request failed.";
        useTimelineStore.getState().setLoading(false);
        useTimelineStore.getState().setError(message);
        return { success: false, error: message };
      }
    },
    [],
  );

  const injectPromptFromPromptLibrary = useCallback(
    async (text: string): Promise<{ success: boolean; error?: string }> => {
      const commands = commandsRef.current;
      if (!commands) {
        return {
          success: false,
          error: "Shell connection not available.",
        };
      }

      try {
        return await commands.injectPrompt(text);
      } catch (error) {
        return {
          success: false,
          error:
            error instanceof Error
              ? error.message
              : "Prompt injection request failed.",
        };
      }
    },
    [],
  );

  const submitSupportRequest = useCallback(
    async (
      input: SupportRequestFormInput,
    ): Promise<ApiResult<{ success: true }>> => {
      const apiClient = runtimeApiClientRef.current;
      if (!apiClient) {
        return {
          success: false,
          error: {
            error: "Remote API client is not available.",
          },
        };
      }

      return apiClient.submitSupportRequest({
        ...input,
        context: {
          conversationName,
          conversationMetadata: metadataText,
          contractVersion: selectedVersion,
          adapterConfigStatus: adapterConfigStatus?.state ?? null,
          adapterPlatform: adapterConfigStatus?.platform ?? null,
          adapterVersion: adapterConfigStatus?.version ?? null,
          isAuthenticated,
          authEmail: sessionEmail,
          authName: sessionName,
          hasAccessToken,
          appVersion: __APP_VERSION__,
          extensionVersion,
          remoteUrl: window.location.href,
          userAgent: window.navigator.userAgent,
          submittedAt: new Date().toISOString(),
        },
      });
    },
    [
      adapterConfigStatus,
      conversationName,
      extensionVersion,
      hasAccessToken,
      isAuthenticated,
      metadataText,
      selectedVersion,
      sessionEmail,
      sessionName,
    ],
  );

  const connect = useCallback(() => {
    resetAuthUiState();
    setIsTroubleshootingOpen(false);
    connectToShell();
  }, [connectToShell, resetAuthUiState]);

  if (!isExtensionRoute) {
    return <CenteredStateView message="This app is intended for /extension." />;
  }

  if (connectionState === "connecting") {
    return <LoadingView />;
  }

  if (connectionState === "rejected") {
    return (
      <CenteredStateView message="Please update the Claudafinil extension." />
    );
  }

  if (connectionState === "timeout") {
    return (
      <CenteredStateView
        message="Failed to connect to the extension shell."
        actionLabel="Retry"
        onAction={connect}
      />
    );
  }

  return (
    <div className="flex h-full min-h-full flex-col bg-background text-foreground">
      <RemoteHeader
        conversationName={conversationName}
        metadataText={metadataText}
        authErrorMessage={authErrorMessage}
        isAuthenticated={isAuthenticated}
        authUser={authUser}
        pendingAuthAction={pendingAuthAction}
        onSignIn={signIn}
        onSignOut={signOut}
        onOpenSupport={() => setIsSupportDialogOpen(true)}
        showTroubleshootingEntry={showTroubleshootingEntry}
        onToggleTroubleshooting={() =>
          setIsTroubleshootingOpen((current) => !current)
        }
      />

      {isTroubleshootingOpen ? (
        <TroubleshootingPanel
          selectedVersion={selectedVersion}
          adapterConfigStatus={adapterConfigStatus}
          hasAccessToken={hasAccessToken}
          onClose={() => setIsTroubleshootingOpen(false)}
        />
      ) : null}

      <KnownIssueBanner />

      <RemoteTabs
        onNavigateToNode={navigateToTimelineNode}
        onNavigateToAnnotation={navigateToAnnotation}
        onInjectPrompt={injectPromptFromPromptLibrary}
        onRefreshConversation={refreshConversationTree}
      />

      <SupportRequestDialog
        open={isSupportDialogOpen}
        sessionEmail={sessionEmail}
        onClose={() => setIsSupportDialogOpen(false)}
        onSubmit={submitSupportRequest}
      />
    </div>
  );
}
